import React, { useCallback, useMemo } from "react";

import styles from "./IntroPage.module.scss";
import { useIntl } from "react-intl";

import { prepareIntroData, SectionItem } from "@src/components/page-template/helper";
import { useUi } from "@jelly/lesson";

import euFooter from "@src/assets/images/eu-cofunded.svg?url";
import classnames from "classnames";
import { nl2p } from "@src/utils/nl2p";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@src/components/layout/MainLayout";
import { Team } from "@src/components/team/Team";
import { messages } from "@src/components/page-template/IntroPage.messages";

interface IntroPageProps {
  onScreenSelect: (screen: string) => void;
  activeScreen?: string;
}

export const IntroPage: React.FC<IntroPageProps> = ({ activeScreen, onScreenSelect }) => {
  if (!activeScreen) {
    activeScreen = "s1"; //default screen
  }

  const intl = useIntl();
  const { Button } = useUi();
  const navigate = useNavigate();

  //to be fetched in the future from course data directly?
  const data = useMemo(() => {
    return prepareIntroData({ intl });
  }, [intl]);

  const flattenedItems = useMemo(() => {
    return data.sections
      .flatMap((s) => [s.mainItem, ...s.courses])
      .filter((item) => item !== undefined) as SectionItem[];
  }, [data]);
  const selectItemById = useCallback(
    (id: string) => {
      return flattenedItems.find((item) => item.id == id);
    },
    [flattenedItems]
  );

  const activeItem = useMemo(
    () => activeScreen && selectItemById(activeScreen),
    [selectItemById, activeScreen]
  ) as SectionItem;

  return (
    <MainLayout>
      <div className={styles.root}>
        <div className={styles.leftColumn}>
          {data.sections.map((s, index) => (
            <div key={index} className={styles.section}>
              <div
                className={classnames(styles.menuHeader, {
                  [styles.active]: s.mainItem?.id === activeScreen,
                  [styles.interactive]: s.mainItem !== undefined,
                })}
                onClick={() => s.mainItem && onScreenSelect(s.mainItem.id)}
              >
                {s.title}
              </div>
              {s.courses.map((chapter) => {
                return (
                  <div
                    key={chapter.number}
                    className={classnames(styles.menuItem, {
                      [styles.active]: chapter.id == activeScreen,
                      [styles.muted]: chapter.muted == true,
                    })}
                    onClick={() => onScreenSelect(chapter.id)}
                  >
                    <span className={styles.denominator}>{chapter.number}.</span>
                    {chapter.title}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {activeItem.subsection && (
          <div className={styles.extraColumn}>
            <div className={styles.menuHeader}>{activeItem.subsection.title}</div>
            {activeItem.subsection.items.map((lesson) => (
              <div
                key={lesson.number}
                className={classnames(styles.menuItem, {
                  [styles.disabled]: lesson.disabled,
                })}
                onClick={() => lesson.href && !lesson.disabled && navigate(lesson.href)}
              >
                <span className={styles.denominator}>{lesson.number}</span>
                {lesson.title}
              </div>
            ))}
          </div>
        )}
        <div className={styles.mainColumn}>
          {activeItem.id == "team" ? (
            <div className={styles.fullWrapper}>
              <Team></Team>
            </div>
          ) : (
            <div className={styles.mainWrapper}>
              <h1>{activeItem.title}</h1>
              {nl2p(activeItem.description)}
              {activeItem.button && (
                <div className={styles.buttonSection}>
                  <Button
                    size={"standard"}
                    theme={"primary"}
                    shape={"normal"}
                    disabled={activeItem.button.disabled}
                    onClick={() => navigate(activeItem.button!.href)}
                  >
                    {activeItem.button.title}
                  </Button>
                  {activeItem.id == "s1" && (
                    <Button
                      size={"standard"}
                      theme={"primary"}
                      shape={"normal"}
                      disabled={activeItem.button.disabled}
                      onClick={() => navigate("/contents?screen=team")}
                    >
                      {intl.formatMessage(messages.goToTeamButton)}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.euFooter}>
            <img src={euFooter} alt={"Co-funded by the EU"} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
